import { AnimatePresence } from './living/presence';
import { m } from './living/elements';
import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, ReactNode, RefObject } from 'react';

import { AccesoDoor, useDoorChecking, useGreeting } from './acceso-door';
import {
  ActionLabel, Arrive, Band, CodeBoxes, Field, markModal, PasswordField, Row, SHAKE, useDoorStill,
} from './acceso-parts';
import { DialogCard, DialogClose } from './living/dialog-card';
import { SPRING } from './living/motion-provider';
import { shake } from './living/shake';
import type { AuthenticationOutcome, CredentialRequest, ReauthenticationRequest } from './runtime-port';

const INVALID_CREDENTIALS = 'El usuario o la contraseña no son correctos.';
const TOO_MANY_ATTEMPTS = 'Demasiados intentos seguidos. Si no recuerdas tu contraseña, pídele a tu responsable que te genere una nueva. Tarda menos de un minuto.';

type AuthenticationFailure = Extract<AuthenticationOutcome, {
  kind: 'invalid_credentials' | 'too_many_attempts' | 'unavailable';
}>;

const unavailableMessages = {
  network: 'No hay conexión. No hemos podido comprobar tus credenciales. Inténtalo de nuevo cuando vuelva.',
  permission: 'No tienes permiso para acceder al Panel.',
  server: 'No se ha podido completar el acceso. Inténtalo de nuevo.',
  invalid_response: 'No se ha podido verificar la respuesta de acceso. Inténtalo de nuevo.',
  expired_session: 'La nueva sesión ya estaba caducada. No hemos abierto el Panel. Inténtalo de nuevo más tarde.',
} as const;

const failureText = (failure: AuthenticationFailure): string => failure.kind === 'unavailable' ? unavailableMessages[failure.reason] : INVALID_CREDENTIALS;
const failureKey = (failure: AuthenticationFailure): string => failure.kind === 'unavailable' ? failure.reason : failure.kind;
const SIX_DIGITS = /^\d{6}$/;
/** How long the drawn tick stays when nothing takes the door away (a spec, a gallery frame). */
const TICK_MS = 1600;

/** Form state only. Identity owns credential proof, lockout and session truth. */
function useAuthenticationAttempt<Request>(
  onAuthenticate: (request: Request) => Promise<AuthenticationOutcome>,
  onSecondFactorRequired: () => void,
) {
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<AuthenticationFailure | null>(null);
  const [credentialRefusals, setCredentialRefusals] = useState(0);
  const [secondFactorRequired, setSecondFactorRequired] = useState(false);
  const [secondFactor, setSecondFactor] = useState('');
  // A refusal shakes the form once; a proof that opened the Panel draws a tick in «Entrar».
  const [refusals, setRefusals] = useState(0);
  const [entered, setEntered] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!entered) return undefined;
    const timer = window.setTimeout(() => setEntered(false), TICK_MS);
    return () => window.clearTimeout(timer);
  }, [entered]);

  const authenticate = async (request: Request): Promise<AuthenticationOutcome | null> => {
    if (inFlight.current) return null;
    inFlight.current = true;
    attempts.current += 1;
    setSubmitting(true);
    setFailure(null);
    try {
      const outcome = await onAuthenticate(request);
      if (!mounted.current) return null;
      switch (outcome.kind) {
        case 'invalid_credentials':
          setCredentialRefusals((count) => count + 1);
          setRefusals((count) => count + 1);
          setFailure(outcome);
          break;
        case 'too_many_attempts':
          setRefusals((count) => count + 1);
          setFailure(outcome);
          break;
        case 'unavailable':
          setFailure(outcome);
          break;
        case 'second_factor_required':
          setSecondFactorRequired(true);
          onSecondFactorRequired();
          break;
        case 'authenticated':
        case 'membership_selection_required':
          setSecondFactor('');
          setCredentialRefusals(0);
          setEntered(true);
          break;
      }
      return outcome;
    } catch {
      if (mounted.current) setFailure({ kind: 'unavailable', reason: 'network' });
      return null;
    } finally {
      inFlight.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  const resetSecondFactor = () => {
    setSecondFactorRequired(false);
    setSecondFactor('');
    setFailure(null);
  };

  // Local validation is feedback, not a server refusal or a lockout attempt.
  const reportIncompleteFields = () => { setFailure({ kind: 'invalid_credentials' }); setRefusals((count) => count + 1); };
  const showRecoveryAdvice = credentialRefusals >= 3 || failure?.kind === 'too_many_attempts';
  const invalid = failure?.kind === 'invalid_credentials' || failure?.kind === 'too_many_attempts';
  const codeIncomplete = secondFactorRequired && !SIX_DIGITS.test(secondFactor);

  return {
    authenticate, submitting, failure, secondFactorRequired, secondFactor, setSecondFactor, resetSecondFactor, reportIncompleteFields,
    showRecoveryAdvice, invalid, codeIncomplete, refusals, entered, attempts,
  };
}

/** What the shared pieces of both forms read from an attempt. */
interface Attempt {
  readonly submitting: boolean;
  readonly invalid: boolean;
  readonly secondFactorRequired: boolean;
  readonly secondFactor: string;
  readonly setSecondFactor: (value: string) => void;
  readonly refusals: number;
  readonly attempts: RefObject<number>;
}

/** One shake per refusal: transform only, once; nothing when nothing may move. */
function useRefusalShake(attempt: Attempt): RefObject<HTMLFormElement | null> {
  const still = useDoorStill();
  const scope = useRef<HTMLFormElement | null>(null);
  const { refusals } = attempt;
  useEffect(() => {
    if (refusals === 0 || still || scope.current === null) return;
    shake(scope.current, { transform: SHAKE }, { duration: 0.44, ease: 'easeInOut' });
  }, [refusals, scope, still]);
  return scope;
}

/**
 * The second factor is sent by itself once its sixth digit lands («Entrar» stays for the keyboard). A press made
 * meanwhile wins: the automatic one is dropped.
 */
function useCodeSentAtSixthDigit(attempt: Attempt, send: () => void, still: boolean) {
  const timer = useRef<number | null>(null);
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);
  return (value: string) => {
    const before = attempt.secondFactor;
    attempt.setSecondFactor(value);
    if (!SIX_DIGITS.test(value) || value === before) return;
    const pressedAt = attempt.attempts.current;
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      if (attempt.attempts.current === pressedAt) sendRef.current();
    }, still ? 0 : 220);
  };
}

function FailureBand({ failure, id }: { readonly failure: AuthenticationFailure | null; readonly id: string }) {
  return (
    <AnimatePresence initial={false}>
      {failure !== null && (
        <Arrive className="acc-form__band" key={`failure-${failureKey(failure)}`}>
          <Band id={id} role="alert" tone="critical">{failureText(failure)}</Band>
        </Arrive>
      )}
    </AnimatePresence>
  );
}

function RecoveryAdvice({ shown }: { readonly shown: boolean }) {
  return (
    <AnimatePresence initial={false}>
      {shown && <Arrive className="acc-form__advice" key="advice"><Band role="status" tone="info">{TOO_MANY_ATTEMPTS}</Band></Arrive>}
    </AnimatePresence>
  );
}

const SWAP = {
  animate: { opacity: 1, transform: 'translateX(0px)' },
  exit: { opacity: 0, transform: 'translateX(-22px)' },
  initial: { opacity: 0, transform: 'translateX(22px)' },
  transition: SPRING.snap,
} as const;

/** The password, or the six boxes that slide in where it was once Identity asks for the code. */
function SecretSwap({ attempt, password, onCode, autoFocusCode = true }: {
  readonly attempt: Attempt;
  readonly password: ReactNode;
  readonly onCode: (value: string) => void;
  readonly autoFocusCode?: boolean;
}) {
  const still = useDoorStill();
  const swap = still ? { animate: SWAP.animate } : SWAP;
  return (
    <AnimatePresence initial={false} mode="popLayout">
      {attempt.secondFactorRequired ? (
        <m.div className="acc-form__swap" key="code" {...swap}>
          <CodeBoxes autoFocus={autoFocusCode} disabled={attempt.submitting} invalid={attempt.invalid} onChange={onCode} value={attempt.secondFactor} />
        </m.div>
      ) : (
        <m.div className="acc-form__swap" key="password" {...swap}>{password}</m.div>
      )}
    </AnimatePresence>
  );
}

/** On a phone the photograph is seen first: one tap opens the keyboard (the identifier is not focused on open). */
const phoneDoor = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(max-width: 760px)').matches;

interface CredentialFormProps {
  readonly onAuthenticate: (request: CredentialRequest) => Promise<AuthenticationOutcome>;
  readonly onSecondFactorRequired: () => void;
  readonly submitLabel?: string;
  /** The form's first row in the door's entrance stagger (the brand and the greeting come before it). */
  readonly firstRow?: number;
}

export interface AccessLoginScreenProps extends CredentialFormProps {
  /** Display only a context already known by the caller; do not guess a tenant before login. */
  readonly contextLabel?: string;
  readonly notice?: string;
  /**
   * docs/decisions/2026-10-28-tpv-sin-conexion.md · what this tablet keeps that has not gone up yet («Hay {n}
   * operaciones sin subir en esta tablet…»): one band above the fields, in the door's caution tone, with the tablet's glyph.
   */
  readonly tabletBand?: string;
}

/**
 * Acceso: the door to the restaurant. The h1 shows the greeting of the Madrid hour; its accessible name stays
 * «Iniciar sesión» (a visually hidden text inside it, the visible greeting `aria-hidden`).
 */
export function AccessLoginScreen({ contextLabel = 'Exylon', notice, tabletBand, ...formProps }: AccessLoginScreenProps) {
  const titleId = useId();
  const greeting = useGreeting();
  return (
    <AccesoDoor context={contextLabel} screenLabel="Acceso" view="login">
      <section aria-labelledby={titleId} className="acc-login">
        <Row index={1}>
          <h1 className="acc-greet" id={titleId}><span className="acc-sr">Iniciar sesión</span><span aria-hidden="true">{greeting}</span></h1>
        </Row>
        <AnimatePresence initial={false}>
          {notice && <Arrive className="acc-login__notice" key="notice"><Band role="status" tone="info">{notice}</Band></Arrive>}
          {tabletBand && <Arrive className="acc-login__notice" key="tablet"><Band glyph="tablet" role="status" tone="caution">{tabletBand}</Band></Arrive>}
        </AnimatePresence>
        <CredentialForm firstRow={2} {...formProps} />
      </section>
    </AccesoDoor>
  );
}

/** @internal Exported only for its spec. */
export function CredentialForm({ onAuthenticate, onSecondFactorRequired, submitLabel = 'Entrar', firstRow = 0 }: CredentialFormProps) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [autoFocus] = useState(() => !phoneDoor());
  const still = useDoorStill();
  const attempt = useAuthenticationAttempt(onAuthenticate, onSecondFactorRequired);
  const scope = useRefusalShake(attempt);
  const helpButton = useRef<HTMLButtonElement>(null);
  const failureId = useId();
  useDoorChecking(attempt.submitting || attempt.entered);

  const latest = useRef({ identifier, password, attempt });
  latest.current = { identifier, password, attempt };
  const send = async () => {
    const { identifier: who, password: secret, attempt: current } = latest.current;
    if (!who.trim() || !secret || (current.secondFactorRequired && !SIX_DIGITS.test(current.secondFactor))) {
      current.reportIncompleteFields();
      return;
    }
    const outcome = await current.authenticate({
      identifier: who,
      password: secret,
      // The current Acceso extract has no device cards. Keep the conservative
      // shared-session policy at the existing typed seam, not an extra surface.
      deviceMode: 'shared',
      ...(current.secondFactorRequired ? { secondFactor: current.secondFactor } : {}),
    });
    if (outcome?.kind === 'authenticated' || outcome?.kind === 'membership_selection_required') setPassword('');
  };
  const onCode = useCodeSentAtSixthDigit(attempt, () => void send(), still);
  const submit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); void send(); };
  const label = attempt.submitting ? 'Comprobando…' : submitLabel;

  return (
    <>
      <form aria-label="Acceso al Panel" className="acc-form" noValidate onSubmit={submit} ref={scope}>
        <FailureBand failure={attempt.failure} id={failureId} />
        <Row index={firstRow}>
          <Field autoComplete="off" autoFocus={autoFocus} describedBy={attempt.invalid ? failureId : undefined} disabled={attempt.submitting}
            inputMode={/^\d/.test(identifier) ? 'numeric' : 'email'} invalid={attempt.invalid} label="Correo o teléfono" name="identifier"
            onChange={(value) => { setIdentifier(value); attempt.resetSecondFactor(); }} placeholder="maria@casaroble.es o 600 000 000" required
            sourceControl="acceso-DOM-C001" testId="access-identifier" value={identifier} />
        </Row>
        <Row className="acc-form__secret" index={firstRow + 1}>
          <SecretSwap attempt={attempt} onCode={onCode} password={
            <PasswordField autoComplete="off" describedBy={attempt.invalid ? failureId : undefined} disabled={attempt.submitting} invalid={attempt.invalid}
              label="Contraseña" name="password" onChange={setPassword} onToggle={() => setPasswordVisible((visible) => !visible)} required
              sourceControl="acceso-DOM-C002" testId="access-password" toggleSourceControl="acceso-DOM-C003" toggleTestId="access-show-password"
              value={password} visible={passwordVisible} />
          } />
        </Row>
        <Row index={firstRow + 2}>
          <button className="acc-submit lv-btn lv-btn--primary lv-press" data-busy={attempt.submitting || attempt.entered || undefined}
            data-source-control="acceso-DOM-C004" data-testid="access-submit" disabled={attempt.submitting || attempt.codeIncomplete} type="submit">
            <ActionLabel text={label} tick={attempt.entered && !attempt.submitting} />
          </button>
        </Row>
        <RecoveryAdvice shown={attempt.showRecoveryAdvice} />
        <Row className="acc-form__help" index={firstRow + 3}>
          <button aria-expanded={helpOpen} aria-haspopup="dialog" className="lv-linkbtn acc-help lv-press" data-lit={attempt.showRecoveryAdvice || undefined}
            data-source-control="acceso-DOM-C005" data-testid="access-help" onClick={() => setHelpOpen(true)} ref={helpButton}
            type="button">¿No puedes entrar?</button>
        </Row>
      </form>
      <HelpDialog onOpenChange={setHelpOpen} open={helpOpen} returnFocusTo={helpButton} />
    </>
  );
}

/**
 * «¿No puedes entrar?»: today's three sentences and one way back, on the living dialog (a sheet from the foot on a
 * phone). As today: `aria-modal`, focus on «Volver a intentarlo» when it opens, and back on «¿No puedes entrar?» when it
 * closes (it is opened by state, so the return is explicit).
 */
function HelpDialog({ open, onOpenChange, returnFocusTo }: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly returnFocusTo: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <DialogCard className="acc-dialog acc-dialog--help"
      footer={
        <DialogClose asChild>
          <button className="lv-btn lv-btn--primary lv-press acc-dialog__wide" data-source-control="acceso-DOM-C009" data-testid="access-help-return"
            type="button">Volver a intentarlo</button>
        </DialogClose>
      }
      onCloseAutoFocus={(event) => { event.preventDefault(); returnFocusTo.current?.focus(); }}
      onOpenChange={onOpenChange} open={open} overlayTestId="access-help-backdrop" testId="access-help-dialog" title="Si no puedes entrar">
      <div className="acc-help-text" ref={markModal}>
        <p>No podemos enviarte la contraseña por correo ni por mensaje. Nadie del sistema la conoce.</p>
        <p>Tu responsable puede generarte una en el momento: entra en Equipo, abre tu ficha y usa «Recuperar acceso».</p>
        <p>En cuanto lo haga, la contraseña anterior deja de funcionar.</p>
      </div>
    </DialogCard>
  );
}

/**
 * D-160 ruling 19 · the boundary dialog's field label follows the credential
 * kind: «Tu PIN» for an account that signs in with a PIN, «Tu contraseña» for
 * one that signs in with a password.
 *
 * Identity serves no credential kind. `GET /auth/current`
 * (`IdentityCurrentStaffSessionResponse` in the generated kernel) returns
 * `user.roles`, `rotation_required` and `two_factor_enabled` and nothing that
 * names the credential; the store behind it keeps one `users.password` bcrypt
 * column for both (`backend/src/database/bootstrap-v4/modules/identity-access.ts`),
 * and `backend/src/modules/identity-access/` has no other read that would say.
 * So the Panel cannot know, and it keeps the neutral word rather than guessing
 * at one the person may not recognise. The label lives here, in one place, so
 * the day the kind is served this is the single value that changes.
 */
const REAUTHENTICATION_CREDENTIAL_LABEL = 'Tu clave de acceso';

export interface ReauthenticationFormProps {
  readonly onAuthenticate: (request: ReauthenticationRequest) => Promise<AuthenticationOutcome>;
  readonly onSecondFactorRequired: () => void;
}

/** The expired session's form: only the person's credential (focused), the same bands, «Entrar y seguir». */
export function ReauthenticationForm({ onAuthenticate, onSecondFactorRequired }: ReauthenticationFormProps) {
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const still = useDoorStill();
  const attempt = useAuthenticationAttempt(onAuthenticate, onSecondFactorRequired);
  const scope = useRefusalShake(attempt);
  const failureId = useId();

  const latest = useRef({ password, attempt });
  latest.current = { password, attempt };
  const send = async () => {
    const { password: secret, attempt: current } = latest.current;
    if (!secret || (current.secondFactorRequired && !SIX_DIGITS.test(current.secondFactor))) {
      current.reportIncompleteFields();
      return;
    }
    const outcome = await current.authenticate({
      password: secret,
      ...(current.secondFactorRequired ? { secondFactor: current.secondFactor } : {}),
    });
    if (outcome?.kind === 'authenticated' || outcome?.kind === 'membership_selection_required') setPassword('');
  };
  const onCode = useCodeSentAtSixthDigit(attempt, () => void send(), still);
  const submit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); void send(); };
  const label = attempt.submitting ? 'Comprobando…' : 'Entrar y seguir';

  return (
    <form aria-label="Confirmar identidad" className="acc-form acc-form--reauth" noValidate onSubmit={submit} ref={scope}>
      <FailureBand failure={attempt.failure} id={failureId} />
      <m.div layout="position" transition={{ layout: SPRING.soft }}>
        <SecretSwap attempt={attempt} onCode={onCode} password={
          <PasswordField autoComplete="off" autoFocus describedBy={attempt.invalid ? failureId : undefined} disabled={attempt.submitting}
            invalid={attempt.invalid} label={REAUTHENTICATION_CREDENTIAL_LABEL} name="password" onChange={setPassword}
            onToggle={() => setPasswordVisible((visible) => !visible)} required value={password} visible={passwordVisible} />
        } />
      </m.div>
      <m.div layout="position" transition={{ layout: SPRING.soft }}>
        <button className="acc-submit lv-btn lv-btn--primary lv-press" data-busy={attempt.submitting || attempt.entered || undefined}
          disabled={attempt.submitting || attempt.codeIncomplete} type="submit">
          <ActionLabel text={label} tick={attempt.entered && !attempt.submitting} />
        </button>
      </m.div>
      <RecoveryAdvice shown={attempt.showRecoveryAdvice} />
    </form>
  );
}
